/**
 * Config and Refine tab content for the Script Writer's bottom panel.
 *
 * These are ScriptWriter-specific rather than the shared `ConfigTabContent` /
 * `RefineTabContent`: those two are built around image generation (style
 * pickers, aspect ratio, quality tiers, reference selectors, retry counts) and
 * neither has a slot for a prompt library. Adding a `scriptWriter` branch to a
 * 39KB shared component to hide almost all of it would be the larger change.
 *
 * What IS shared is the shell — both render inside `UnifiedBottomPanel`, so
 * positioning, zoom behaviour and the read-only treatment stay common.
 *
 * Both dropdowns go through `FlyoutPortal` rather than an inline
 * `absolute bottom-full`. The panel's tab-content area is `overflow-hidden`
 * (UnifiedBottomPanel), so an in-flow popover opening upward is clipped at the
 * panel edge — and the prompt library is taller than the panel is. FlyoutPortal
 * renders to document.body at z-[150] and also brings placement flipping,
 * viewport clamping, Escape and canvas-viewport dismissal.
 */

import { memo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../../lib/utils'
import { CREATIVE_TEXT_MAX_LENGTH } from '../shared/descriptionLimits'
import { FlyoutPortal } from '../shared/FlyoutPortal'
import { getScriptModelLabel } from '../../../../constants/modelAliases'
import { looksLikeFullScreenplay } from './screenplayText'
import {
  SCRIPT_PROMPT_LIBRARY,
  localizeScriptPromptTitle,
  type ScriptPromptLibraryEntry,
} from './scriptPromptLibrary'

/**
 * The two script models the node can generate with, in display order.
 *
 * Written as an explicit union plus a `readonly` array rather than
 * `[...] as const`: `apps/console` forbids all `as` expressions and
 * `check-forbidden-ts-syntax.mjs` counts `as const` among them. Same shape as
 * `RULES_UPDATE_ACTOR_VALUES` and `CREDIT_CARD_BRAND_ORDER`.
 */
export type ScriptModelKey = 'gemini' | 'claude'
export const SCRIPT_MODEL_KEYS: readonly ScriptModelKey[] = ['gemini', 'claude']

// ─── Shared bits ──────────────────────────────────────────────────────────────

const SubmitButton = memo(function SubmitButton({
  onClick,
  disabled,
  isRunning,
  title,
}: {
  onClick: () => void
  disabled: boolean
  isRunning: boolean
  title: string
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) onClick()
      }}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid="script-panel-submit"
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors nodrag',
        disabled
          ? 'cursor-not-allowed bg-surface-2 text-text-muted'
          : 'bg-primary text-primary-foreground hover:bg-primary/90',
      )}
    >
      {isRunning ? (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      ) : (
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      )}
    </button>
  )
})

const PromptField = memo(function PromptField({
  value,
  onChange,
  placeholder,
  testId,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
  testId: string
}) {
  const { t } = useTranslation('canvas')
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5">
      <label className="shrink-0 text-xxs uppercase tracking-wider text-text-muted">
        {t('scriptWriterNode.prompt')}
      </label>
      <div className="relative flex min-h-0 flex-1 rounded-lg border border-border-subtle bg-surface-0">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, CREATIVE_TEXT_MAX_LENGTH))}
          placeholder={placeholder}
          maxLength={CREATIVE_TEXT_MAX_LENGTH}
          className={cn(
            'w-full resize-none bg-transparent px-3 py-2 pb-6 text-xs font-medium leading-relaxed',
            'text-foreground placeholder-text-muted/50 focus:outline-none',
            'nodrag nowheel',
          )}
          onWheel={(e) => e.stopPropagation()}
          data-testid={testId}
        />
        <span className="pointer-events-none absolute bottom-1.5 right-3 text-xxs text-text-muted/60">
          {value.length} / {CREATIVE_TEXT_MAX_LENGTH}
        </span>
      </div>
    </div>
  )
})

/**
 * Model chip + its popover.
 *
 * Shared because BOTH tabs carry it in the design — Config at Figma 133:453 and
 * Refine at 133:518. Refine needs it because a regenerate can legitimately
 * switch model, and having to leave the tab to do that is exactly the detour
 * the panel exists to remove.
 */
const ScriptModelChip = memo(function ScriptModelChip({
  model,
  onModelChange,
}: {
  model: ScriptModelKey
  onModelChange: (model: ScriptModelKey) => void
}) {
  const { t } = useTranslation('canvas')
  const [open, setOpen] = useState(false)
  const chipRef = useRef<HTMLButtonElement>(null)

  return (
    <div className="relative">
      <button
        ref={chipRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((prev) => !prev)
        }}
        aria-expanded={open}
        data-testid="script-model-chip"
        className="flex h-8 items-center gap-1.5 rounded-full border border-border-subtle px-3 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-2 hover:text-foreground nodrag"
      >
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span>{getScriptModelLabel(model)}</span>
        <svg
          className="h-2.5 w-2.5 text-text-muted"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <FlyoutPortal
        triggerRef={chipRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        minWidth={0}
        testId="script-model-menu"
      >
        <div>
          <span className="mb-1.5 block text-xxs uppercase tracking-wider text-text-muted">
            {t('scriptWriterNode.model')}
          </span>
          <div className="flex items-center gap-1.5">
            {SCRIPT_MODEL_KEYS.map((key) => {
              const isSelected = key === model
              return (
                <button
                  key={key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onModelChange(key)
                    setOpen(false)
                  }}
                  data-testid={`script-model-option-${key}`}
                  data-active={isSelected || undefined}
                  className={cn(
                    'whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors nodrag',
                    isSelected
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'border-border-subtle text-text-secondary hover:bg-surface-2 hover:text-foreground',
                  )}
                >
                  {getScriptModelLabel(key)}
                </button>
              )
            })}
          </div>
        </div>
      </FlyoutPortal>
    </div>
  )
})

// ─── Config tab ───────────────────────────────────────────────────────────────

interface ScriptConfigTabProps {
  prompt: string
  onPromptChange: (value: string) => void
  /** Model the next run will use. */
  model: ScriptModelKey
  onModelChange: (model: ScriptModelKey) => void
  onGenerate: () => void
  isRunning: boolean
  submitDisabled: boolean
}

const ScriptConfigTab = memo(function ScriptConfigTab({
  prompt,
  onPromptChange,
  model,
  onModelChange,
  onGenerate,
  isRunning,
  submitDisabled,
}: ScriptConfigTabProps) {
  const { t } = useTranslation('canvas')

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 p-3" data-testid="script-config-tab">
      <PromptField
        value={prompt}
        onChange={onPromptChange}
        placeholder={t('scriptWriterNode.describeStory')}
        testId="script-prompt-input"
      />

      <div className="flex shrink-0 items-center justify-between gap-2">
        <ScriptModelChip model={model} onModelChange={onModelChange} />

        <SubmitButton
          onClick={onGenerate}
          disabled={submitDisabled}
          isRunning={isRunning}
          title={t('scriptWriterNode.generateScript')}
        />
      </div>
    </div>
  )
})

// ─── Refine tab ───────────────────────────────────────────────────────────────

interface ScriptRefineTabProps {
  feedbackText: string
  onFeedbackTextChange: (value: string) => void
  onSubmit: () => void
  /**
   * Take the box contents as the new screenplay wholesale instead of as a
   * revision note. Offered only when the text parses as a screenplay.
   */
  onReplaceScript: () => void
  /**
   * Mirrors every state `onReplaceScript` refuses. Separate from
   * `submitDisabled`, which only covers the ordinary refine run.
   */
  replaceDisabled: boolean
  isRunning: boolean
  submitDisabled: boolean
  /** Model the regenerate will use — Refine carries the chip too (Figma 133:518). */
  model: ScriptModelKey
  onModelChange: (model: ScriptModelKey) => void
}

const ScriptRefineTab = memo(function ScriptRefineTab({
  feedbackText,
  onFeedbackTextChange,
  onSubmit,
  onReplaceScript,
  replaceDisabled,
  isRunning,
  submitDisabled,
  model,
  onModelChange,
}: ScriptRefineTabProps) {
  const { t } = useTranslation('canvas')
  const [libraryOpen, setLibraryOpen] = useState(false)
  const libraryRef = useRef<HTMLButtonElement>(null)

  // Pasting a whole screenplay and pasting a revision note are opposite
  // intents, and the backend edit prompt can only honour the second. Ask
  // rather than guess (Workplace post 1597061732051124).
  const pastedScreenplay = looksLikeFullScreenplay(feedbackText)

  // Replace rather than append: the library entries are complete standalone
  // instructions, and concatenating two of them produces contradictory
  // direction ("compress by 30%" + "expand the cold open") that the model
  // silently splits the difference on.
  const applyEntry = (entry: ScriptPromptLibraryEntry) => {
    onFeedbackTextChange(entry.prompt)
    setLibraryOpen(false)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 p-3" data-testid="script-refine-tab">
      <PromptField
        value={feedbackText}
        onChange={onFeedbackTextChange}
        placeholder={t('scriptWriterNode.describeChanges')}
        testId="refine-feedback-textarea"
      />

      {pastedScreenplay && (
        <div
          data-testid="script-refine-replace-prompt"
          className="shrink-0 rounded-lg border border-border-subtle bg-surface-2 px-3 py-2"
        >
          <p className="text-xs font-medium text-text-secondary">
            {t('scriptWriterNode.pastedScreenplayDetected')}
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onReplaceScript()
            }}
            disabled={replaceDisabled}
            data-testid="script-refine-replace-button"
            className="mt-2 rounded-full border border-border-subtle px-3 py-1 text-xxs uppercase tracking-wider text-text-secondary transition-colors hover:bg-surface-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 nodrag"
          >
            {t('scriptWriterNode.replaceWholeScript')}
          </button>
        </div>
      )}

      {/* Library, then model chip, then a spacer pushing submit right — the
          Refine bar has three controls, so `justify-between` no longer does it. */}
      <div className="flex shrink-0 items-center gap-2">
        <div className="relative">
          <button
            ref={libraryRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setLibraryOpen((prev) => !prev)
            }}
            aria-expanded={libraryOpen}
            data-testid="script-prompt-library-toggle"
            className="flex h-8 items-center gap-2 rounded-full border border-border-subtle px-3 text-xxs uppercase tracking-wider text-text-secondary transition-colors hover:bg-surface-2 hover:text-foreground nodrag"
          >
            <span>{t('scriptWriterNode.promptLibraryLabel')}</span>
            <svg
              className="h-2.5 w-2.5 text-text-muted"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <FlyoutPortal
            triggerRef={libraryRef}
            isOpen={libraryOpen}
            onClose={() => setLibraryOpen(false)}
            minWidth={280}
            testId="script-prompt-library-menu"
          >
            <div onWheel={(e) => e.stopPropagation()}>
              <span className="mb-1.5 block px-1 text-xxs uppercase tracking-wider text-text-muted">
                {t('scriptWriterNode.promptLibraryLabel')}
              </span>
              <div className="flex flex-col gap-1">
                {SCRIPT_PROMPT_LIBRARY.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      applyEntry(entry)
                    }}
                    title={entry.prompt}
                    data-testid={`script-prompt-library-${entry.id}`}
                    className="rounded-lg border border-border-subtle px-3 py-2 text-left text-xs font-medium text-text-secondary transition-colors hover:bg-surface-2 hover:text-foreground nodrag"
                  >
                    {localizeScriptPromptTitle(entry, t)}
                  </button>
                ))}
              </div>
            </div>
          </FlyoutPortal>
        </div>

        <ScriptModelChip model={model} onModelChange={onModelChange} />

        <div className="flex-1" />

        <SubmitButton
          onClick={onSubmit}
          disabled={submitDisabled}
          isRunning={isRunning}
          title={t('scriptWriterNode.regenerate')}
        />
      </div>
    </div>
  )
})

ScriptConfigTab.displayName = 'ScriptConfigTab'
ScriptRefineTab.displayName = 'ScriptRefineTab'

export { ScriptConfigTab, ScriptRefineTab }
